/**
 * WP-V2-SG — katalog obiektów Stajni (PLAN-V2 §6 Stable).
 * Silnik: interact/; scena konsumuje ObjectLayer + AnimatedDogAvatar + world.
 */
import type { ObjectDef, SnapZone, SpawnPlacement } from '../../interact/types.js';
import { MIN_OBJECT_SIZE } from '../../interact/types.js';
import type { ColliderRect } from '../../core/world/index.js';

/** Świat stajni — wnętrze 1600×720 (PLAN-V2 §4.2). */
export const STABLE_WORLD_W = 1600;
export const STABLE_WORLD_H = 720;

export const STABLE_HAY_DEF_ID = 'stable_hay';
export const STABLE_BRUSH_DEF_ID = 'stable_brush';
export const STABLE_CARROT_DEF_ID = 'stable_carrot';
export const STABLE_WHEELBARROW_DEF_ID = 'stable_wheelbarrow';
export const STABLE_HORSESHOE_DEF_ID = 'stable_horseshoe';
export const STABLE_QUEST_ID = 'stable_feed_horses';

/** Katalog 2 koników (bez Phaser — testowalny). */
export type HorseCatalogEntry = {
  id: string;
  namePl: string;
  color: number;
  x: number;
  y: number;
  snapZoneId: 'stable_horse_a' | 'stable_horse_b';
};

export const HORSE_CATALOG: readonly HorseCatalogEntry[] = [
  {
    id: 'horse-iskierka',
    namePl: 'Iskierka',
    color: 0xd4a574,
    x: 900,
    y: 400,
    snapZoneId: 'stable_horse_a',
  },
  {
    id: 'horse-chmurka',
    namePl: 'Chmurka',
    color: 0xeceff1,
    x: 1150,
    y: 430,
    snapZoneId: 'stable_horse_b',
  },
] as const;

export function horseCatalog(): readonly HorseCatalogEntry[] {
  return HORSE_CATALOG;
}

export const STABLE_LABELS_PL: Record<string, string> = {
  stable_hay: 'Bele siana',
  stable_brush: 'Szczotka',
  stable_carrot: 'Marchewka',
  stable_wheelbarrow: 'Taczka',
  stable_horseshoe: 'Podkowa',
  stable_bucket: 'Wiadro',
  stable_apple: 'Jabłko',
  stable_ladder: 'Drabina',
};

export const STABLE_ICONS: Record<string, string> = {
  stable_hay: '🌾',
  stable_brush: '🪮',
  stable_carrot: '🥕',
  stable_wheelbarrow: '🛒',
  stable_horseshoe: '🧲',
  stable_bucket: '🪣',
  stable_apple: '🍎',
  stable_ladder: '🪜',
};

/** Katalog ObjectDef — cele ≥64 px. */
export function stableObjectDefs(): ObjectDef[] {
  const size = Math.max(MIN_OBJECT_SIZE, 72);
  return [
    {
      id: STABLE_HAY_DEF_ID,
      texture: 'obj_stable_hay',
      size: Math.max(MIN_OBJECT_SIZE, 80),
      interactions: ['pickup', 'give', 'consume', 'drag'],
      states: ['full', 'empty'],
      sound: 'chomp',
      fx: 'crumbs',
      dogReaction: 'eat',
      snapTargets: ['stable_wheelbarrow', 'stable_horse_a', 'stable_horse_b'],
    },
    {
      id: STABLE_BRUSH_DEF_ID,
      texture: 'obj_stable_brush',
      size,
      interactions: ['tap', 'drag'],
      sound: 'brush',
      fx: 'hearts',
      dogReaction: 'happy',
      snapTargets: ['stable_horse_a', 'stable_horse_b'],
    },
    {
      id: STABLE_CARROT_DEF_ID,
      texture: 'obj_stable_carrot',
      size: MIN_OBJECT_SIZE,
      interactions: ['pickup', 'give', 'consume', 'drag'],
      states: ['full', 'empty'],
      sound: 'crunch',
      fx: 'crumbs',
      dogReaction: 'eat',
      snapTargets: ['stable_horse_a', 'stable_horse_b'],
    },
    {
      id: STABLE_WHEELBARROW_DEF_ID,
      texture: 'obj_stable_wheelbarrow',
      size: Math.max(MIN_OBJECT_SIZE, 96),
      interactions: ['drag', 'tap'],
      fx: 'sparkle',
    },
    {
      id: STABLE_HORSESHOE_DEF_ID,
      texture: 'obj_stable_horseshoe',
      size,
      interactions: ['tap'],
      sound: 'clang',
      fx: 'stars',
      dogReaction: 'jump',
    },
    {
      id: 'stable_bucket',
      texture: 'obj_stable_bucket',
      size,
      interactions: ['tap', 'toggle'],
      states: ['full', 'empty'],
      fx: 'splash',
      dogReaction: 'sniff',
    },
    {
      id: 'stable_apple',
      texture: 'obj_stable_apple',
      size: MIN_OBJECT_SIZE,
      interactions: ['pickup', 'give', 'consume', 'drag'],
      fx: 'hearts',
      dogReaction: 'eat',
      snapTargets: ['stable_horse_a', 'stable_horse_b'],
    },
    {
      id: 'stable_ladder',
      texture: 'obj_stable_ladder',
      size: Math.max(MIN_OBJECT_SIZE, 88),
      interactions: ['tap'],
      fx: 'sparkle',
      dogReaction: 'sniff',
    },
  ];
}

/** Rozmieszczenie w stajni 1600×720. */
export function stablePlacements(): SpawnPlacement[] {
  return [
    { defId: STABLE_HAY_DEF_ID, x: 280, y: 480 },
    { defId: STABLE_HAY_DEF_ID, x: 360, y: 500 },
    { defId: STABLE_BRUSH_DEF_ID, x: 200, y: 360 },
    { defId: STABLE_CARROT_DEF_ID, x: 480, y: 420 },
    { defId: STABLE_CARROT_DEF_ID, x: 540, y: 440 },
    { defId: STABLE_WHEELBARROW_DEF_ID, x: 720, y: 500 },
    { defId: STABLE_HORSESHOE_DEF_ID, x: 1400, y: 280 },
    { defId: 'stable_bucket', x: 180, y: 500 },
    { defId: 'stable_apple', x: 600, y: 400 },
    { defId: 'stable_ladder', x: 1500, y: 360 },
  ];
}

/** Snap: taczka + strefy koni (give/feed). */
export function stableSnapZones(
  horseA = { x: 900, y: 400 },
  horseB = { x: 1150, y: 430 },
): SnapZone[] {
  return [
    { id: 'stable_wheelbarrow', x: 720, y: 500, radius: 64 },
    { id: 'stable_horse_a', x: horseA.x, y: horseA.y, radius: 80 },
    { id: 'stable_horse_b', x: horseB.x, y: horseB.y, radius: 80 },
  ];
}

export const STABLE_COLLIDERS: ColliderRect[] = [
  { x: 0, y: 0, width: STABLE_WORLD_W, height: 100 },
  { x: 600, y: 140, width: 400, height: 40 }, // ściana stodoły
];

/** Assety PNG oczekiwane (placeholdery do artu). */
export function stableMissingAssetKeys(): string[] {
  const keys = new Set<string>();
  for (const d of stableObjectDefs()) {
    keys.add(d.texture);
    if (d.states) {
      for (const s of d.states) keys.add(`obj_${d.id}_${s}`);
    }
  }
  keys.add('horse_idle');
  keys.add('horse_walkA');
  keys.add('horse_walkB');
  keys.add('horse_eat');
  keys.add('horse_nod');
  return [...keys].sort();
}

// ─── Czysta logika (E2E unit) ────────────────────────────────────────────────

export type FeedKind = 'hay' | 'carrot' | 'apple';

export function isFeedDef(defId: string): boolean {
  return (
    defId === STABLE_HAY_DEF_ID ||
    defId === STABLE_CARROT_DEF_ID ||
    defId === 'stable_apple'
  );
}

export function feedKindOf(defId: string): FeedKind | null {
  if (defId === STABLE_HAY_DEF_ID) return 'hay';
  if (defId === STABLE_CARROT_DEF_ID) return 'carrot';
  if (defId === 'stable_apple') return 'apple';
  return null;
}

/**
 * Nakarm konika z ręki (give siana/marchewki).
 * Zwraca efekt: je + parska + emote.
 */
export function feedHorseFromHand(
  horseId: string,
  alreadyFed: ReadonlySet<string>,
  defId: string,
): {
  ok: boolean;
  horseId: string;
  action: 'eat' | null;
  snort: boolean;
  hearts: boolean;
  newlyFed: boolean;
  fedIds: string[];
  missionComplete: boolean;
} {
  if (!isFeedDef(defId)) {
    return {
      ok: false,
      horseId,
      action: null,
      snort: false,
      hearts: false,
      newlyFed: false,
      fedIds: [...alreadyFed],
      missionComplete: alreadyFed.size >= 2,
    };
  }
  const next = new Set(alreadyFed);
  const newlyFed = !next.has(horseId);
  next.add(horseId);
  const fedIds = [...next];
  return {
    ok: true,
    horseId,
    action: 'eat',
    snort: true,
    hearts: true,
    newlyFed,
    fedIds,
    missionComplete: fedIds.length >= 2,
  };
}

/** Czesanie szczotką → serduszka. */
export function brushHorse(defId: string): { hearts: boolean; action: 'happy' | null } {
  if (defId !== STABLE_BRUSH_DEF_ID) return { hearts: false, action: null };
  return { hearts: true, action: 'happy' };
}

/** Przybij podkowę → dźwięk clang. */
export function nailHorseshoe(defId: string): { sound: 'clang' | null; stars: boolean } {
  if (defId !== STABLE_HORSESHOE_DEF_ID) return { sound: null, stars: false };
  return { sound: 'clang', stars: true };
}

/** Przewiezienie siana taczką (drag siana na snap wheelbarrow). */
export function haulHayOnWheelbarrow(
  snapZoneId: string | undefined,
  defId: string,
): boolean {
  return defId === STABLE_HAY_DEF_ID && snapZoneId === 'stable_wheelbarrow';
}

export function countInteractiveKinds(defs: ObjectDef[] = stableObjectDefs()): number {
  return defs.filter((d) => d.interactions.length > 0).length;
}

export function allDefsMeetMinTouch(defs: ObjectDef[] = stableObjectDefs()): boolean {
  return defs.every((d) => d.size >= MIN_OBJECT_SIZE);
}

export function countHayPlacements(
  placements: readonly SpawnPlacement[] = stablePlacements(),
): number {
  return placements.filter((p) => p.defId === STABLE_HAY_DEF_ID).length;
}
